/**
 * 「確認」でよい改定の告知（contexts/PolicyNoticeContext.tsx が開く）
 *
 * 見出しは「◯◯を改定しました」（「一部改定」のような軽い言い方にしない）。主な変更点・影響・全文と
 * 改定履歴へのリンクを出し、「内容を確認しました」で確認したことを記録する。「あとで」で閉じられる。
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Modal } from '@/components/common/Modal';
import PolicyChangeSummary from '@/components/policy/PolicyChangeSummary';
import { POLICY_LABEL } from '@/constants/policyRevisions';
import { useUser } from '@/hooks/useUser';
import { updateUserConsent } from '@/services/consent';
import type { PolicyDocStatus } from '@/utils/policyStatus';

interface PolicyUpdateNoticeProps {
  open: boolean;
  statuses: PolicyDocStatus[];
  onLater: () => void;
  onAcknowledged: () => void;
}

export default function PolicyUpdateNotice({ open, statuses, onLater, onAcknowledged }: PolicyUpdateNoticeProps) {
  const { refreshUser } = useUser();
  const [saving, setSaving] = useState(false);
  const title = `${statuses.map((s) => POLICY_LABEL[s.doc]).join('と')}を改定しました`;

  const acknowledge = async () => {
    setSaving(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error('認証トークンが取得できませんでした');
      await updateUserConsent(
        token,
        Object.fromEntries(statuses.map((s) => [s.doc, true])),
        { action: 'acknowledge', source: 'policy_notice' },
      );
      await refreshUser();
      toast.success('改定内容を確認しました');
      onAcknowledged();
    } catch (error) {
      console.error('Failed to record policy acknowledgement:', error);
      toast.error('記録できませんでした。もう一度お試しください。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onLater} title={title} maxWidth="lg">
      <div className="space-y-6">
        {statuses.map((status) => (
          <PolicyChangeSummary key={status.doc} status={status} />
        ))}
        <p className="text-sm text-text-600">
          内容を確認すると、AIアシスタントが使えるようになります。それ以外の機能は、確認しなくてもこれまでどおり使えます。
        </p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onLater}
            disabled={saving}
            className="rounded-lg px-4 py-2 text-sm text-text-600 hover:bg-text-100"
          >
            あとで
          </button>
          <button
            type="button"
            onClick={acknowledge}
            disabled={saving}
            className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? '記録しています…' : '内容を確認しました'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
